import type { Book } from "../types/api";

// Which reconciliation BOOK the app is working in, client-side.
//
// The book is not a preference like the theme — it selects which layouts, which stores
// and which insurer list every later call reads, so it has to be resolved BEFORE the
// upload screen asks for insurers. The server is the boundary (every gated route
// re-checks it per request); this module only decides what to show and what to send.
//
// Mirrors insurer_layout_detector.books: "AP" is the default and the original book, and
// an absent value means AP everywhere.

export const DEFAULT_BOOK: Book = "AP";

// sessionStorage, not localStorage, and the same contract as lib/sessionRestore: the
// chosen book is per-tab working state, so a shared machine cannot drop the next user
// into the book the last one picked. Cleared on logout/401 alongside the stored session.
const KEY = "cri_recon_book_v1";

/** The book a session should open in, or null meaning "ask the user".
 *
 * Kept pure and free of storage/DOM so it can be reasoned about and tested directly
 * (tests_ar/book_port.py is a line-for-line port; keep the two in lockstep).
 *
 *   no books at all         -> null. The account holds nothing; the caller says so
 *                              rather than showing an empty picker.
 *   exactly one book        -> that book, resolved silently. A single-book account never
 *                              sees a chooser, so an AP-only user's app is byte-for-byte
 *                              the experience it was before AR existed.
 *   several, none stored    -> null: ASK. Deliberately NOT defaulted to AP — a dual-book
 *                              user dropped silently into AP can reconcile an entire
 *                              month against the wrong book and the figures look real.
 *   several, one stored     -> the stored book, but only while it is still held. A book
 *                              revoked since (the gate is re-read per request, so this
 *                              really happens mid-session) falls back to asking; it is
 *                              never widened back to AP, which would hand AP access to
 *                              an account an admin had just moved off it.
 */
export function resolveBook(books: Book[], stored: Book | null): Book | null {
  if (!books.length) return null;
  if (books.length === 1) return books[0];
  if (stored && books.includes(stored)) return stored;
  return null;
}

/** Does this account need the AP/AR switch in the header at all? */
export function showsBookSwitch(books: Book[]): boolean {
  return books.length > 1;
}

export function loadBook(): Book | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    // Validated against the vocabulary rather than trusted: a hand-edited or
    // stale value must read as "nothing chosen", never as a book.
    return raw === "AP" || raw === "AR" ? raw : null;
  } catch {
    return null;   // storage disabled (private mode) — the user is simply asked again
  }
}

export function saveBook(book: Book): void {
  try {
    sessionStorage.setItem(KEY, book);
  } catch {
    /* a forgotten choice costs one extra click, never a wrong run */
  }
}

export function clearBook(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
